import React, { useEffect, useState, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

// === Colors (consistent schema) ===
const GREEN = "#2e7d32";   // bars: sprzedaż
const ORANGE = "#fb8c00";  // bars: liczba transakcji
const BLUE = "#2196f3";    // line: średnia wartość

// Styled components
const Wrapper = styled.div`
  background-color: #f9f9f9;
  padding: 20px;
  width: 100%;
  height: 100%;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const FilterRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #444;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 500px;
`;

const formatNumber = (value) =>
  value?.toLocaleString("pl-PL").replace(/,/g, " ");

const RevenueByCitiesHeatmap = ({ salesData, clientsData }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("00");

  const availableYears = useMemo(() => {
    const years = new Set();
    salesData.forEach((row) => {
      const year = parseInt(row.Rok, 10);
      if (!isNaN(year)) years.add(year);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [salesData]);

  useEffect(() => {
    if (availableYears.length > 0 && selectedYear === null) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  useEffect(() => {
    if (!selectedYear) return;

    const computeRevenue = (row) =>
      parseFloat(row.K_15) ||
      parseFloat(row.K_19) ||
      parseFloat(row.K_10) ||
      parseFloat(row.K_21) ||
      parseFloat(row.K_17) ||
      parseFloat(row.K_11) ||
      parseFloat(row.K_22) ||
      0;

    const filtered = salesData.filter((row) => {
      const year = row.Rok?.toString();
      const month = row.Miesiac?.toString().padStart(2, "0");
      return (
        year === selectedYear.toString() &&
        (selectedMonth === "00" || month === selectedMonth)
      );
    });

    const kontrahentaToCity = {};
    clientsData.forEach((c) => {
      if (c.nip?.trim()) {
        kontrahentaToCity[c.nip.trim()] = c.miejscowosc || "Nieznane";
      }
    });

    const aggregated = filtered.reduce((acc, row) => {
      const revenue = computeRevenue(row);
      const nip = row.NrKontrahenta?.trim();
      const city = kontrahentaToCity[nip] || "Nieznane";

      if (!acc[city]) {
        acc[city] = { city, totalRevenue: 0, transactions: 0 };
      }
      acc[city].totalRevenue += revenue;
      acc[city].transactions += 1;
      return acc;
    }, {});

    const finalData = Object.values(aggregated)
      .filter((item) => item.city !== "Nieznane")
      .map((item) => ({
        ...item,
        avgValue:
          item.transactions > 0 ? item.totalRevenue / item.transactions : 0,
      }))
      .sort((a, b) => b.totalRevenue - a.totalRevenue)
      .slice(0, 20); // top 20 cities

    setFilteredData(finalData);
  }, [salesData, clientsData, selectedYear, selectedMonth]);

  if (!filteredData.length) {
    return <p style={{ textAlign: "center" }}>Ładowanie danych...</p>;
  }

  const chartData = {
    labels: filteredData.map((c) => c.city),
    datasets: [
      {
        label: "Całkowita sprzedaż",
        data: filteredData.map((c) => c.totalRevenue),
        backgroundColor: GREEN,
        borderColor: GREEN,
        borderWidth: 1,
        yAxisID: "y",
        type: "bar",
      },
      {
        label: "Liczba transakcji",
        data: filteredData.map((c) => c.transactions),
        backgroundColor: ORANGE,
        borderColor: ORANGE,
        borderWidth: 1,
        yAxisID: "y1",
        type: "bar",
      },
      {
        label: "Średnia wartość transakcji",
        data: filteredData.map((c) => c.avgValue),
        borderColor: BLUE,
        backgroundColor: "rgba(33,150,243,0.15)",
        yAxisID: "y2",
        type: "line",
        tension: 0.3,
        fill: true,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      tooltip: {
        backgroundColor: "#333",
        titleColor: "#fff",
        bodyColor: "#fff",
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${formatNumber(context.raw)}`
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Miasta" },
        ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 },
      },
      y: {
        position: "left",
        title: { display: true, text: "Całkowita sprzedaż" }, // changed
        ticks: { callback: (value) => formatNumber(value) },
      },
      y1: {
        position: "right",
        title: { display: true, text: "Liczba transakcji" },
        grid: { drawOnChartArea: false },
        ticks: { callback: (value) => formatNumber(value) },
      },
      y2: {
        position: "right",
        offset: true,
        title: { display: true, text: "Średnia wartość" },
        grid: { drawOnChartArea: false },
        ticks: { callback: (value) => formatNumber(value) },
      },
    },
  };

  return (
    <Wrapper>
      <TitleWrapper>
        Porównanie sprzedaży w miastach
        <InfoIconWrapper data-tooltip-id="cities-tooltip">
          <Info />
        </InfoIconWrapper>
      </TitleWrapper>
      <InfoTooltip
        id="cities-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Porównanie sprzedaży w miastach</strong><br/>
            Wykres pokazuje całkowitą sprzedaż,<br/>
            liczbę transakcji i średnią wartość dla miast.
          </>
        }
      />

      <FilterRow>
        <FilterLabel>Rok:</FilterLabel>
        <Select value={selectedYear ?? ""} onChange={(e) => setSelectedYear(e.target.value)}>
          {availableYears.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </Select>

        <FilterLabel>Miesiąc:</FilterLabel>
        <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
          <option value="00">Cały rok</option>
          {[...Array(12).keys()].map((m) => {
            const val = String(m + 1).padStart(2, "0");
            const monthName = new Date(2000, m).toLocaleString("pl-PL", { month: "long" });
            return (
              <option key={val} value={val}>{monthName}</option>
            );
          })}
        </Select>
      </FilterRow>

      <ChartContainer>
        <Bar data={chartData} options={chartOptions} />
      </ChartContainer>
    </Wrapper>
  );
};

export default RevenueByCitiesHeatmap;
